import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BookingAddOns, BookingInput, ValidationResult } from "../../../interfaces/booking.interface";
import { Itinerary, Tour } from "../../../interfaces/itinerary.interface";
import { addons, childAgeLabel, CRUISE_CATEGORY, currencyDisplay, validateBooking, withMalaysiaFirst, parsePricingTiers, getPriceTierForPassenger, getPriceTierForPassengerWithType } from "../../../utils/helpers";
import { Button, Spin, Modal } from "antd";
import Icon from "@mdi/react";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { PAYMENT_TERMS, priceTypes, pricingCategory } from "../../../enums/pricing.enum";
import { format, parse } from "date-fns";
import { PromotionDeduction } from "../../../interfaces/promotion.interface";
interface TravellerCounterProps {
  selectedTravelers: string[];
  itinerary: Itinerary;
  travelerCounts: Record<string, number>;
  updatePrice?: (event: any) => void;
  selectedTour: Tour;
  cruiseMultiplier: number | undefined;
  showTotal: boolean;
  currentTotal: string;
  promotionCalcLoading?: boolean;
  priceBreakdown: {
    key: string;
    label: string;
    category: string;
    count: number;
    sum: number;
    tiers: {
        price_type: string;
        price: number;
        count: number;
        sum: number;
    }[] | undefined;
  }[];
  bookingAddons: BookingAddOns[];
  nonMalaysiaChargeAmount: number;
  promotionDeductions?: PromotionDeduction[];
  showAdminFee: boolean;
  adminFeeShown?: number;
  fareType?: PAYMENT_TERMS;
  remainingBalance?: number;
  setTravelerCounts: React.Dispatch< React.SetStateAction<Record<string, number>> >;
  setSelectedTravelers: React.Dispatch< React.SetStateAction<string[]> >;
  onDone?: () => void;
}

const TravellerCounter: React.FC<TravellerCounterProps> = ({
  selectedTravelers,
  itinerary,
  travelerCounts,
  updatePrice,
  selectedTour,
  cruiseMultiplier,
  showTotal = false,
  promotionCalcLoading = false,
  currentTotal,
  priceBreakdown,
  bookingAddons,
  nonMalaysiaChargeAmount,
  promotionDeductions,
  showAdminFee,
  adminFeeShown,
  fareType,
  remainingBalance,
  setTravelerCounts,
  setSelectedTravelers,
  onDone,
}) => {
  const checkoutDisabled = itinerary.checkoutEnabled === false;
  const maxBookingSeats = selectedTour.max_booking_seats || 20;
  const lastPromoRef = useRef("");
  
  const [showPriceSummaryModal, setShowPriceSummaryModal] = useState(false);
  const totalPax = selectedTravelers.length;
  const visaDeduction = Number(selectedTour.visa_fee || 0) * totalPax;
  
  const pricingCategories = useMemo(
    () => pricingCategory(selectedTour?.room_types || "series"),
    [selectedTour?.room_types]
  );
  const pricingTiers = useMemo(
    () => parsePricingTiers(selectedTour.prices || []),
    [selectedTour.prices]
  );
  const getPriceForPassenger = (passengerIndex: number, cat: string) =>
      getPriceTierForPassengerWithType(pricingTiers, passengerIndex, cat);
  const paxPriceItems = useMemo(
    () => selectedTravelers.map((cat, index) => ({ category: cat, ...getPriceForPassenger(index + 1, cat) })),
    [selectedTravelers, pricingTiers]
  );
  
  const bookingInputParams: BookingInput = useMemo(() => ({
    adult: travelerCounts["adult"] || 0,
    single_supplement: travelerCounts["single_supplement"] || 0,
    child_twin: travelerCounts["child_twin"] || 0,
    child_with_bed: travelerCounts["child_with_bed"] || 0,
    child_no_bed: travelerCounts["child_no_bed"] || 0,
    allowTripleRoom: selectedTour.allow_triple_room || false,
  }), [travelerCounts, selectedTour.allow_triple_room]);
  const hasCabinNavigation = !!itinerary.cabinNavigation;
  const paxMultiplier = hasCabinNavigation ? (cruiseMultiplier || 1) : (selectedTour.booking_pax_multiplier || 1);
  const validation = useMemo(
    () => (hasCabinNavigation ? { isValid: true, message: "" } : validateBooking(bookingInputParams, false)),
    [bookingInputParams, hasCabinNavigation]
  );
  
  const hadleChangesOnTraveller = (key: string) => {
    updatePrice?.({
      promo: paxPriceItems[0].type,
      items: paxPriceItems.map((item) => ({
        category: item.category,
        price: item.price,
        price_type: item.type,
      })),
    });
  }
  const handleAddTraveler = (key: string) => {
    if (selectedTravelers.length + paxMultiplier > maxBookingSeats) return;
    setTravelerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + paxMultiplier }));
    setSelectedTravelers(prev => [...prev, ...Array(paxMultiplier).fill(key)]);
  };
  const handleRemoveTraveler = (key: string) => {
    // Only remove a full booking group, and never let a count go negative.
    if ((travelerCounts[key] || 0) < paxMultiplier) return;
    setTravelerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - paxMultiplier) }));
    setSelectedTravelers(prev => {
      const updated = [...prev];
      let removed = 0;
      for (let i = updated.length - 1; i >= 0 && removed < paxMultiplier; i--) {
        if (updated[i] === key) { updated.splice(i, 1); removed++; }
      }
      return updated;
    });
    if(selectedTravelers.length === 1)
    {
      lastPromoRef
      const items = paxPriceItems.filter(
        (item) => item.category === selectedTravelers[0]
      );
      lastPromoRef.current = items[0]?.type || "";
    }
  };
  useEffect(() => {
    if(updatePrice != undefined)
    {
      if (selectedTravelers.length === 0) {
        updatePrice({
          promo: lastPromoRef.current,
          items: [],
        });
        return;
      }
      hadleChangesOnTraveller(selectedTravelers[selectedTravelers.length-1]);
    }
  }, [selectedTravelers, paxPriceItems]);
  
  return (
    <>
      <div className="booking-form_content">
        <div className="policy-notice">
            <div className="policy-notice_icon"><img src="/iceholidays-assets/images/v3/policy-notice-icon.svg" alt="" aria-hidden="true" width={32} height={32} /></div>
            <div className="policy-notice_text">
            <span className="policy-notice_title">Child Age Policy</span>
            <span className="policy-notice_body">
                Traveller age is based on the departure date. If the child turns older by departure day, the higher age category applies.
            </span>
            </div>
        </div>
        <div className="pax-list">
          {Object.entries(pricingCategories).map(([key, label]) => {
              const age = childAgeLabel(key, itinerary.child_age_ranges);
              return (
              <div key={key} className="pax-row">
              <div className="pax-row_info">
                  <span className="pax-row_label">{String(label)}</span>
                  {age && <span className="pax-row_age">{age}</span>}
              </div>
              <div className="pax-row_controls">
                  <Button shape="circle" className="pax-btn pax-btn--minus" icon={<Icon path={mdiMinus} size="18px" />} onClick={() => handleRemoveTraveler(key)} disabled={(travelerCounts[key] || 0) < paxMultiplier} />
                  <span className="pax-count">{travelerCounts[key] || 0}</span>
                  <Button shape="circle" className="pax-btn pax-btn--plus" icon={<Icon path={mdiPlus} size="18px" />} onClick={() => handleAddTraveler(key)} disabled={selectedTravelers.length + paxMultiplier > maxBookingSeats} />
              </div>
              </div>
              );
          })}
          {selectedTravelers.length > 0 && !validation.isValid && (
            <div className="validation-error">
              <img src="/iceholidays-assets/images/v3/policy-error-icon.svg" alt="" aria-hidden="true" width={24} height={24} />
              <span>{validation.message}</span>
            </div>
          )}
        </div>
        {showTotal && 
            <div className="booking-form_bottom-bar">
              <div className="booking-form_summary">
                <span className="booking-form_summary-label">
                  Total
                </span>
                <span className="booking-form_summary-total">{promotionCalcLoading ? <Spin size="small" /> : currentTotal}</span>
                <small className="bottom-note">(incl. taxes &amp; fees)</small>
                <div><button type="button" className="booking-form_summary_price-breakdown" onClick={()=> setShowPriceSummaryModal(true)}>
                  Price breakdown
                </button>
                </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  className="booking-form_submit-btn"
                  disabled={checkoutDisabled || promotionCalcLoading || !validation.isValid}
                  onClick={onDone}
                >
                  Done
                </Button>
            </div>
        }
      </div>
      <Modal
        title={<span className="section-label">Price Breakdown</span>}
        open={showPriceSummaryModal}
        onCancel={() => setShowPriceSummaryModal(false)}
        footer={null}
        wrapClassName={`v3 price-breakdown`}
        className="price-breakdown-modal v3"
        centered
      >
        <div className="price-summary">
          {priceBreakdown?.map(({ key, label, category, count, sum, tiers}) => (
              tiers?.map(({count, price, price_type, sum}) => (
                <div key={key} className="price-summary_row">
                  <span className="price-summary_label">{price_type ? `${price_type.charAt(0).toUpperCase() + price_type.slice(1)} ${label ?? category}` : label ?? category} x {count}</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, sum)}
                  </span>
                </div>
              ))
          ))}
              {bookingAddons?.map((addon) => (
                <div key={addon.label} className="price-summary_row">
                  <span className="price-summary_label">{addon.label}</span>
                  <span className="price-summary_value">
                    {addon.totalPax} x {currencyDisplay(itinerary.priceCurrency, addon.perPaxValue)}
                  </span>
                </div>
              ))}
              {nonMalaysiaChargeAmount > 0 && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Non-Malaysian Charge</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, nonMalaysiaChargeAmount)}
                  </span>
                </div>
              )}
              {visaDeduction > 0 && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Visa Fee</span>
                  <span className="price-summary_value">
                    - {currencyDisplay(itinerary.priceCurrency, visaDeduction)}
                  </span>
                </div>
              )}
              {!promotionCalcLoading && promotionDeductions?.map((promo) => (
                <div key={promo.id} className="price-summary_row">
                  <span className="price-summary_label">{promo.title} x {promo.entitledPaxCount}</span>
                  <span className="price-summary_value">
                    - {currencyDisplay(itinerary.priceCurrency, promo.amount)}
                  </span>
                </div>
              ))}
              {showAdminFee && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Admin Fee</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, adminFeeShown)}
                  </span>
                </div>
              )}
              <div className="price-summary_divider" />
              <div className="price-summary_row price-summary_row--total">
                <span className="price-summary_label">
                  {fareType === PAYMENT_TERMS.DEPOSIT ? "Total Due Now" : "Grand Total"}
                </span>
                <span className="price-summary_value">{promotionCalcLoading ? <Spin size="small" /> : currentTotal}</span>
              </div>
              {fareType === PAYMENT_TERMS.DEPOSIT && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Remaining Balance</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, remainingBalance)}
                  </span>
                </div>
              )}
            </div>
      </Modal>
    </>
  );
};

export default TravellerCounter;